import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { plans } from '@/lib/client';
import { usePlansStore } from '@/stores/plansStore';

/** A local datetime for the input, from a Date. */
function localInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** D15: when an API-only plan runs on its own; the backend checks the time again (at least a minute out). */
export function ScheduleDialog({ plan, onClose }: { plan: plans.PlanView; onClose: () => void }) {
  const schedule = usePlansStore((s) => s.schedule);
  const busy = usePlansStore((s) => s.busy.schedule ?? false);
  const [at, setAt] = useState(localInputValue(new Date(Date.now() + 5 * 60_000)));
  const tooSoon = new Date(at).getTime() < Date.now() + 60_000;
  const submit = async () => {
    if (await schedule(plan.id, new Date(at).toISOString())) onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">Schedule "{plan.title}"</h3>
        <p className="mt-1 text-sm text-muted-foreground">Every step runs through the API, so nobody needs to be at the browser. It runs as you, at:</p>
        <input
          type="datetime-local"
          value={at}
          min={localInputValue(new Date(Date.now() + 60_000))}
          onChange={(e) => setAt(e.target.value)}
          className="mt-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
        />
        {tooSoon && <p className="mt-1 text-xs text-amber-400">Pick a time at least a minute from now.</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-secondary">Cancel</button>
          <button onClick={() => void submit()} disabled={busy || tooSoon} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Schedule
          </button>
        </div>
      </div>
    </div>
  );
}
